import type { ReactNode } from 'react';
import { useFormat, type Formatter } from '@paige/format';
import { CompanyName } from '../TestMark';
import { COST_ESTIMATE_EXPLAINED, MARGIN_EXPLAINED, dollarsAPage } from './CostEstimate';
import { costAPage, margin, pageOf } from './revenue-math';
import type { Revenue, RevenueDir, RevenueRow, RevenueSort } from './revenue-types';
import '../insights/insights.css';

/** The router's link to a company's page; the name alone when the board is shown without a router. */
export type RenderCompany = (tenant: string, name: string) => ReactNode;

export interface RevenueTableProps {
  revenue: Revenue;
  sort: RevenueSort;
  dir: RevenueDir;
  /** A heading was pressed: the ledger sorts, the page only asks. */
  onSort: (sort: RevenueSort, dir: RevenueDir) => void;
  /** Previous or Next: the offset of the page to read. */
  onPage: (offset: number) => void;
  renderLink: RenderCompany;
  /** The next page is being read: the rows shown are the last answer's. */
  busy?: boolean | undefined;
}

interface Column {
  heading: string;
  /** What the ledger sorts this column by; a column without one is not sortable. */
  sort?: RevenueSort;
  numeric: boolean;
  cell: (row: RevenueRow, f: Formatter) => ReactNode;
}

function marginCell(row: RevenueRow, f: Formatter): ReactNode {
  const m = margin(row);
  if (m === null) return <span className="ad-muted">no revenue</span>;
  return (
    <span className={m < 30 ? 'ad-danger' : ''} title={MARGIN_EXPLAINED}>
      {f.percent(m, 0)}
    </span>
  );
}

function costAPageCell(row: RevenueRow, f: Formatter): ReactNode {
  const perPage = costAPage(row);
  return perPage === null ? '—' : dollarsAPage(f, perPage);
}

const COLUMNS: readonly Column[] = [
  { heading: 'Pages read', sort: 'pages', numeric: true, cell: (r, f) => f.int(r.pagesRead) },
  { heading: 'Collected', sort: 'net', numeric: true, cell: (r, f) => f.money(r.net) },
  { heading: 'Processing cost', sort: 'cost', numeric: true, cell: (r, f) => f.money(r.cost) },
  { heading: 'Cost per page', sort: 'costPerPage', numeric: true, cell: costAPageCell },
  { heading: 'Margin', numeric: true, cell: marginCell },
];

const TITLES: Partial<Record<string, string>> = {
  Collected: 'Card money net of refunds.',
  'Processing cost': COST_ESTIMATE_EXPLAINED,
  'Cost per page': COST_ESTIMATE_EXPLAINED,
  Margin: MARGIN_EXPLAINED,
};

/** A heading that asks the ledger for its order: largest first on the first press of a number, A to Z of a name. */
function Heading(props: {
  heading: string;
  sort?: RevenueSort | undefined;
  numeric: boolean;
  table: Pick<RevenueTableProps, 'sort' | 'dir' | 'onSort'>;
}): ReactNode {
  const { heading, sort, numeric, table } = props;
  const className = numeric ? 'is-numeric' : undefined;
  if (!sort) {
    return (
      <th scope="col" className={className} title={TITLES[heading]}>
        {heading}
      </th>
    );
  }
  const on = table.sort === sort;
  const first: RevenueDir = numeric ? 'desc' : 'asc';
  const next: RevenueDir = on ? (table.dir === 'desc' ? 'asc' : 'desc') : first;
  const spoken = on ? (table.dir === 'desc' ? 'descending' : 'ascending') : 'none';
  return (
    <th scope="col" aria-sort={spoken} className={className} title={TITLES[heading]}>
      <button type="button" onClick={() => table.onSort(sort, next)}>
        {heading}
        <span aria-hidden="true" className="ad-sort__arrow">
          {on ? (table.dir === 'desc' ? '↓' : '↑') : '↕'}
        </span>
      </button>
    </th>
  );
}

/** "Showing 1–50 of 312", with Previous and Next. */
function Pager({ revenue, onPage }: Pick<RevenueTableProps, 'revenue' | 'onPage'>): ReactNode {
  const f = useFormat();
  const page = pageOf({ ...revenue, shown: revenue.rows.length });
  return (
    <div className="ad-rtable__pager">
      <span aria-live="polite">
        Showing {f.int(page.first)}–{f.int(page.last)} of {f.int(revenue.total)}
      </span>
      <button
        type="button"
        disabled={page.previous === null}
        onClick={() => onPage(page.previous ?? 0)}
      >
        Previous
      </button>
      <button type="button" disabled={page.next === null} onClick={() => onPage(page.next ?? 0)}>
        Next
      </button>
    </div>
  );
}

/**
 * The companies with anything in the period, a page at a time, in the order the ledger sorted them. A
 * heading asks for another order; nothing is sorted or summed in the browser.
 */
export function RevenueTable(props: RevenueTableProps): ReactNode {
  const { revenue, renderLink } = props;
  const f = useFormat();
  if (revenue.total === 0)
    return <p className="ad-rtable__none">No company read a page or paid in this period.</p>;
  return (
    <div className="ad-rtable" aria-busy={props.busy ?? false}>
      <div className="ad-rtable__scroll">
        <table className="ad-sort">
          <caption className="pui-visually-hidden">
            Pages read, money collected and processing cost per company
          </caption>
          <thead>
            <tr>
              <Heading heading="Company" sort="name" numeric={false} table={props} />
              {COLUMNS.map((c) => (
                <Heading key={c.heading} {...c} table={props} />
              ))}
            </tr>
          </thead>
          <tbody>
            {revenue.rows.map((row) => (
              <tr key={row.tenantId}>
                <th scope="row">
                  <CompanyName name={renderLink(row.tenantId, row.name)} test={row.testCompany} />
                </th>
                {COLUMNS.map((c) => (
                  <td key={c.heading} className="is-numeric">
                    {c.cell(row, f)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager revenue={revenue} onPage={props.onPage} />
    </div>
  );
}
